import React, { useState } from 'react';
import {
  Title,
  Group,
  Button,
  Text,
  Stack,
  Card,
  Badge,
  Avatar,
  SimpleGrid,
  TextInput,
  Select,
  ActionIcon,
  Grid,
  Tabs,
  Alert,
  Modal,
  Divider,
  ThemeIcon,
  Textarea,
  Timeline,
  Indicator,
  MultiSelect,
  Checkbox,
  ScrollArea,
  Box,
  Anchor,
  Center,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconSearch,
  IconFilter,
  IconRefresh,
  IconChevronLeft,
  IconSend,
  IconMessage,
  IconBell,
  IconAlertCircle,
  IconStar,
  IconArchive,
  IconInbox,
  IconPaperclip,
  IconPhoto,
  IconFileText,
  IconBroadcast,
  IconTemplate,
  IconMoodHappy,
  IconCalendar,
  IconPhone,
  IconVideo,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  useTeacherClasses,
  useTeacherMessages,
  useSendParentMessage,

} from '../../hooks/useTeacher';
import { SmartLoader } from '../../components/common/LoadingStates';

// Message-related interfaces
interface MessageThread {
  id: string;
  participants: {
    parent: { id: string; name: string; email: string };
    student: { id: string; name: string };
  };
  messages: Message[];
  last_message: Message;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  thread_id: string;
  sender_type: 'teacher' | 'parent';
  sender_id: string;
  sender_name: string;
  content: string;
  attachments?: Attachment[];
  timestamp: string;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface Attachment {
  id: string;
  type: 'image' | 'document' | 'video';
  name: string;
  url: string;
  size: number;
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'general' | 'event' | 'reminder' | 'urgent';
  target: 'all' | 'class' | 'selected';
  class_ids?: string[];
  student_ids?: string[];
  created_at: string;
  scheduled_for?: string;
  status: 'draft' | 'scheduled' | 'sent';
  read_count: number;
  total_recipients: number;
}

interface MessageTemplate {
  id: string;
  name: string;
  category: string;
  subject: string;
  content: string;
  variables?: string[];
}

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'inbox' | 'sent' | 'announcements'>('inbox');
  const [selectedThread, setSelectedThread] = useState<MessageThread | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [selectedMessages, setSelectedMessages] = useState<string[]>([]);
  const [composeModalOpened, { open: openComposeModal, close: closeComposeModal }] = useDisclosure(false);
  const [announcementModalOpened, { open: openAnnouncementModal, close: closeAnnouncementModal }] = useDisclosure(false);
  const [templateModalOpened, { open: openTemplateModal, close: closeTemplateModal }] = useDisclosure(false);

  // Form states
  const [composeForm, setComposeForm] = useState({
    recipient_type: 'parent',
    parent_id: '',
    student_id: '',
    subject: '',
    content: '',
    priority: 'medium' as const,
    attachments: [] as File[],
  });

  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    type: 'general' as const,
    target: 'all' as 'all' | 'class',
    class_ids: [] as string[],
    scheduled_for: null as Date | null,
  });

  const [replyContent, setReplyContent] = useState('');

  const sendMessageMutation = useSendParentMessage();

  const {
    data: classes,
  } = useTeacherClasses();

  const {
    isLoading: messagesLoading,
    error: messagesError,
    refetch: refetchMessages,
  } = useTeacherMessages();

  // Enhanced data fetching using real API
  // const {
  //   data: enhancedMessages,
  //   isLoading: enhancedMessagesLoading,
  //   error: enhancedMessagesError,
  // } = useTeacherMessagesFromAPI({
  //   limit: 50,
  // });

  // const {
  //   data: availableTeachers,
  //   isLoading: teachersLoading,
  // } = useAvailableTeachers();

  // Mock data for threads
  const mockThreads: MessageThread[] = [
    {
      id: 'thread-1',
      participants: {
        parent: { id: 'parent-1', name: 'Sarah Johnson', email: 'sarah.j@email.com' },
        student: { id: 'student-1', name: 'Emma Johnson' },
      },
      messages: [
        {
          id: 'message-1',
          thread_id: 'thread-1',
          sender_type: 'parent',
          sender_id: 'parent-1',
          sender_name: 'Sarah Johnson',
          content: 'Hi Ms. Smith, I wanted to ask about Emma\'s progress in reading. She seems to be struggling a bit at home.',
          timestamp: '2024-03-20T10:30:00',
          read: true,
          priority: 'medium',
        },
        {
          id: 'message-2',
          thread_id: 'thread-1',
          sender_type: 'teacher',
          sender_id: 'teacher-1',
          sender_name: 'Ms. Smith',
          content: 'Thank you for reaching out! I\'ve noticed Emma is making good progress with letter recognition. I\'ll send home some extra practice sheets.',
          timestamp: '2024-03-20T14:15:00',
          read: true,
          priority: 'medium',
        },
      ],
      last_message: {
        id: 'message-3',
        thread_id: 'thread-1',
        sender_type: 'parent',
        sender_id: 'parent-1',
        sender_name: 'Sarah Johnson',
        content: 'That would be wonderful, thank you so much!',
        timestamp: '2024-03-20T15:45:00',
        read: false,
        priority: 'medium',
      },
      unread_count: 1,
      created_at: '2024-03-20T10:30:00',
      updated_at: '2024-03-20T15:45:00',
    },
    {
      id: 'thread-2',
      participants: {
        parent: { id: 'parent-2', name: 'Mike Davis', email: 'mike.d@email.com' },
        student: { id: 'student-2', name: 'Liam Davis' },
      },
      messages: [],
      last_message: {
        id: 'message-4',
        thread_id: 'thread-2',
        sender_type: 'parent',
        sender_id: 'parent-2',
        sender_name: 'Mike Davis',
        content: 'Is it okay if Liam brings his favorite toy for show and tell tomorrow?',
        timestamp: '2024-03-21T08:00:00',
        read: false,
        priority: 'low',
      },
      unread_count: 1,
      created_at: '2024-03-21T08:00:00',
      updated_at: '2024-03-21T08:00:00',
    },
  ];

  // Mock announcements
  const mockAnnouncements: Announcement[] = [
    {
      id: 'announcement-1',
      title: 'Spring Festival Next Week',
      content: 'Dear parents, our Spring Festival will be held next Friday...',
      type: 'event',
      target: 'all',
      created_at: '2024-03-19T09:00:00',
      status: 'sent',
      read_count: 15,
      total_recipients: 28,
    },
    {
      id: 'announcement-2',
      title: 'Picture Day Reminder',
      content: 'Don\'t forget that picture day is tomorrow! Please ensure...',
      type: 'reminder',
      target: 'class',
      class_ids: ['class-1'],
      created_at: '2024-03-20T14:00:00',
      scheduled_for: '2024-03-21T07:00:00',
      status: 'scheduled',
      read_count: 0,
      total_recipients: 18,
    },
  ];

  // Mock templates
  const messageTemplates: MessageTemplate[] = [
    {
      id: 'template-1',
      name: 'Welcome Message',
      category: 'General',
      subject: 'Welcome to [ClassName]!',
      content: 'Dear [ParentName],\n\nWelcome to our class! I\'m excited to have [StudentName] join us...',
      variables: ['ClassName', 'ParentName', 'StudentName'],
    },
    {
      id: 'template-2',
      name: 'Behavior Update',
      category: 'Behavior',
      subject: 'Update on [StudentName]\'s Behavior',
      content: 'Dear [ParentName],\n\nI wanted to share some observations about [StudentName]\'s behavior today...',
      variables: ['ParentName', 'StudentName'],
    },
    {
      id: 'template-3',
      name: 'Achievement Celebration',
      category: 'Achievement',
      subject: 'Celebrating [StudentName]\'s Achievement!',
      content: 'Dear [ParentName],\n\nI\'m thrilled to share that [StudentName] achieved [Achievement] today!',
      variables: ['ParentName', 'StudentName', 'Achievement'],
    },
  ];

  const filteredThreads = mockThreads.filter(thread => {
    const matchesSearch =
      thread.participants.parent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      thread.participants.student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      thread.last_message.content.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesPriority = priorityFilter === 'all' || thread.last_message.priority === priorityFilter;

    return matchesSearch && matchesPriority;
  });

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'urgent': return <IconAlertCircle size={16} />;
      case 'event': return <IconCalendar size={16} />;
      case 'reminder': return <IconBell size={16} />;
      default: return <IconMessage size={16} />;
    }
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'urgent': return 'red';
      case 'event': return 'blue';
      case 'reminder': return 'yellow';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      case 'low': return 'gray';
      default: return 'gray';
    }
  };

  const handleThreadClick = (thread: MessageThread) => {
    setSelectedThread(thread);
    // Mark messages as read
    if (thread.unread_count > 0) {
      // In real app, make API call to mark as read
      refetchMessages();
    }
  };

  const handleSendReply = () => {
    if (!selectedThread || !replyContent.trim()) return;

    sendMessageMutation.mutate({
      parentId: selectedThread.participants.parent.id,
      subject: `Re: ${selectedThread.last_message.content.substring(0, 30)}...`,
      message: replyContent,
    });

    setReplyContent('');
    notifications.show({
      title: 'Reply Sent',
      message: 'Your message has been sent successfully',
      color: 'green',
    });
  };

  const handleSendNewMessage = () => {
    sendMessageMutation.mutate({
      parentId: composeForm.parent_id,
      subject: composeForm.subject,
      message: composeForm.content,
    });

    closeComposeModal();
    setComposeForm({
      recipient_type: 'parent',
      parent_id: '',
      student_id: '',
      subject: '',
      content: '',
      priority: 'medium',
      attachments: [],
    });
  };

  const handleSendAnnouncement = () => {
    notifications.show({
      title: 'Announcement Created',
      message: announcementForm.scheduled_for
        ? 'Your announcement has been scheduled'
        : 'Your announcement has been sent',
      color: 'green',
    });

    closeAnnouncementModal();
    setAnnouncementForm({
      title: '',
      content: '',
      type: 'general',
      target: 'all',
      class_ids: [],
      scheduled_for: null,
    });
  };

  const handleUseTemplate = (template: MessageTemplate) => {
    setComposeForm(prev => ({
      ...prev,
      subject: template.subject,
      content: template.content,
    }));
    closeTemplateModal();
    openComposeModal();
  };

  const toggleSelectMessage = (threadId: string) => {
    setSelectedMessages(prev =>
      prev.includes(threadId)
        ? prev.filter(id => id !== threadId)
        : [...prev, threadId]
    );
  };

  const stats = {
    unread: mockThreads.filter(t => t.unread_count > 0).length,
    total: mockThreads.length,
    urgent: mockThreads.filter(t => t.last_message.priority === 'urgent').length,
    announcements: mockAnnouncements.length,
  };

  return (
    <Stack gap="lg" h="100vh">
      {/* Header */}
      <Card withBorder>
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>Messages</Title>
              <Text size="sm" c="dimmed">
                Communicate with parents and send announcements
              </Text>
            </div>
          </Group>
          <Group>
            <Button
              leftSection={<IconMessage size={16} />}
              onClick={openComposeModal}
            >
              New Message
            </Button>
            <Button
              variant="light"
              leftSection={<IconBroadcast size={16} />}
              onClick={openAnnouncementModal}
            >
              Announcement
            </Button>
            <ActionIcon variant="subtle" onClick={() => refetchMessages()}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
        </Group>
      </Card>

      {/* Statistics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">Unread</Text>
              <Text fw={700} size="xl">{stats.unread}</Text>
            </div>
            <ThemeIcon color="blue" variant="light" size="xl">
              <IconInbox size={24} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Messages</Text>
              <Text fw={700} size="xl">{stats.total}</Text>
            </div>
            <ThemeIcon color="green" variant="light" size="xl">
              <IconMessage size={24} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">Urgent</Text>
              <Text fw={700} size="xl">{stats.urgent}</Text>
            </div>
            <ThemeIcon color="red" variant="light" size="xl">
              <IconAlertCircle size={24} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">Announcements</Text>
              <Text fw={700} size="xl">{stats.announcements}</Text>
            </div>
            <ThemeIcon color="violet" variant="light" size="xl">
              <IconBroadcast size={24} />
            </ThemeIcon>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Main Content */}
      <Grid style={{ flex: 1, overflow: 'hidden' }}>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder h="100%" p={0} style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Tabs */}
            <Box p="md" pb={0}>
              <Tabs value={activeTab} onChange={(value) => setActiveTab(value as any)}>
                <Tabs.List grow>
                  <Tabs.Tab value="inbox" leftSection={<IconInbox size={16} />}>
                    Inbox
                    {stats.unread > 0 && (
                      <Badge size="xs" color="red" ml={4}>{stats.unread}</Badge>
                    )}
                  </Tabs.Tab>
                  <Tabs.Tab value="sent" leftSection={<IconSend size={16} />}>
                    Sent
                  </Tabs.Tab>
                  <Tabs.Tab value="announcements" leftSection={<IconBroadcast size={16} />}>
                    Announcements
                  </Tabs.Tab>
                </Tabs.List>
              </Tabs>
            </Box>

            {/* Filters */}
            <Box p="md" pt="sm">
              <Stack gap="sm">
                <TextInput
                  placeholder="Search messages..."
                  leftSection={<IconSearch size={16} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {activeTab !== 'announcements' && (
                  <Select
                    placeholder="Filter by priority"
                    leftSection={<IconFilter size={16} />}
                    data={[
                      { value: 'all', label: 'All Priorities' },
                      { value: 'urgent', label: 'Urgent' },
                      { value: 'high', label: 'High' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'low', label: 'Low' },
                    ]}
                    value={priorityFilter}
                    onChange={(value) => setPriorityFilter(value || 'all')}
                  />
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Message List */}
            <ScrollArea style={{ flex: 1 }} p="md">
              <SmartLoader
                isLoading={messagesLoading}
                error={messagesError as Error | null}
                isEmpty={activeTab === 'announcements' ? !mockAnnouncements.length : !filteredThreads.length}
                emptyState={
                  <Alert color="blue" title="No messages">
                    {activeTab === 'announcements'
                      ? "No announcements have been sent yet."
                      : searchQuery || priorityFilter !== 'all'
                      ? "No messages match your filters."
                      : "Your inbox is empty."
                    }
                  </Alert>
                }
              >
                {activeTab === 'announcements' ? (
                  <Stack gap="sm">
                    {mockAnnouncements.map(announcement => (
                      <Card
                        key={announcement.id}
                        withBorder
                        radius="md"
                        style={{ cursor: 'pointer' }}
                      >
                        <Group justify="space-between" mb="xs">
                          <Group gap="xs">
                            <ThemeIcon
                              color={getMessageTypeColor(announcement.type)}
                              variant="light"
                              size="sm"
                            >
                              {getMessageTypeIcon(announcement.type)}
                            </ThemeIcon>
                            <Text fw={500} size="sm">{announcement.title}</Text>
                          </Group>
                          <Badge
                            color={announcement.status === 'sent' ? 'green' : 'blue'}
                            size="xs"
                          >
                            {announcement.status}
                          </Badge>
                        </Group>
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {announcement.content}
                        </Text>
                        <Group justify="space-between" mt="xs">
                          <Text size="xs" c="dimmed">
                            {new Date(announcement.created_at).toLocaleDateString()}
                          </Text>
                          {announcement.status === 'sent' && (
                            <Text size="xs" c="dimmed">
                              Read by {announcement.read_count}/{announcement.total_recipients}
                            </Text>
                          )}
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Stack gap="xs">
                    {filteredThreads.map(thread => (
                      <Card
                        key={thread.id}
                        withBorder
                        radius="md"
                        p="sm"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: thread.unread_count > 0 ? 'var(--mantine-color-blue-0)' : undefined,
                        }}
                        onClick={() => handleThreadClick(thread)}
                      >
                        <Group justify="space-between" align="flex-start">
                          <Group align="flex-start" style={{ flex: 1 }}>
                            <Checkbox
                              checked={selectedMessages.includes(thread.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelectMessage(thread.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Avatar size="md" radius="xl">
                              {(thread.participants.parent.name || '').split(' ').map(n => n[0]).join('')}
                            </Avatar>
                            <div style={{ flex: 1 }}>
                              <Group justify="space-between">
                                <div>
                                  <Text size="sm" fw={thread.unread_count > 0 ? 600 : 400}>
                                    {thread.participants.parent.name}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    Re: {thread.participants.student.name}
                                  </Text>
                                </div>
                                <Badge
                                  color={getPriorityColor(thread.last_message.priority)}
                                  size="xs"
                                  variant="light"
                                >
                                  {thread.last_message.priority}
                                </Badge>
                              </Group>
                              <Text size="sm" lineClamp={2} mt={4}>
                                {thread.last_message.content}
                              </Text>
                              <Text size="xs" c="dimmed" mt={4}>
                                {new Date(thread.last_message.timestamp).toLocaleString()}
                              </Text>
                            </div>
                          </Group>
                          {thread.unread_count > 0 && (
                            <Indicator color="blue" size={8} />
                          )}
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                )}
              </SmartLoader>
            </ScrollArea>
          </Card>
        </Grid.Col>

        {/* Message Detail */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card withBorder h="100%" p={0} style={{ display: 'flex', flexDirection: 'column' }}>
            {selectedThread ? (
              <>
                {/* Thread Header */}
                <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                  <Group justify="space-between">
                    <Group>
                      <Avatar size="lg" radius="xl">
                        {(selectedThread.participants.parent.name || '').split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <div>
                        <Text fw={500}>{selectedThread.participants.parent.name}</Text>
                        <Group gap="xs">
                          <Text size="sm" c="dimmed">
                            Parent of {selectedThread.participants.student.name}
                          </Text>
                          <Text size="sm" c="dimmed">•</Text>
                          <Anchor size="sm" component="a" href={`mailto:${selectedThread.participants.parent.email}`}>
                            {selectedThread.participants.parent.email}
                          </Anchor>
                        </Group>
                      </div>
                    </Group>
                    <Group>
                      <ActionIcon variant="subtle">
                        <IconPhone size={18} />
                      </ActionIcon>
                      <ActionIcon variant="subtle">
                        <IconVideo size={18} />
                      </ActionIcon>
                      <ActionIcon variant="subtle">
                        <IconStar size={18} />
                      </ActionIcon>
                      <ActionIcon variant="subtle">
                        <IconArchive size={18} />
                      </ActionIcon>
                    </Group>
                  </Group>
                </Box>

                {/* Messages */}
                <ScrollArea style={{ flex: 1 }} p="md">
                  <Timeline active={-1} bulletSize={24} lineWidth={2}>
                    {[...selectedThread.messages, selectedThread.last_message].map((message) => (
                      <Timeline.Item
                        key={message.id}
                        bullet={
                          <Avatar size={24} radius="xl">
                            {message.sender_name[0]}
                          </Avatar>
                        }
                        title={
                          <Group gap="xs">
                            <Text size="sm" fw={500}>{message.sender_name}</Text>
                            <Badge
                              size="xs"
                              variant="light"
                              color={message.sender_type === 'teacher' ? 'blue' : 'gray'}
                            >
                              {message.sender_type}
                            </Badge>
                          </Group>
                        }
                      >
                        <Card withBorder p="sm" mt="xs">
                          <Text size="sm">{message.content}</Text>
                          {message.attachments && message.attachments.length > 0 && (
                            <Group gap="xs" mt="sm">
                              {message.attachments.map(attachment => (
                                <Badge
                                  key={attachment.id}
                                  variant="light"
                                  leftSection={
                                    attachment.type === 'image' ? <IconPhoto size={12} /> :
                                    attachment.type === 'video' ? <IconVideo size={12} /> :
                                    <IconFileText size={12} />
                                  }
                                >
                                  {attachment.name}
                                </Badge>
                              ))}
                            </Group>
                          )}
                        </Card>
                        <Text size="xs" c="dimmed" mt={4}>
                          {new Date(message.timestamp).toLocaleString()}
                        </Text>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </ScrollArea>

                {/* Reply Box */}
                <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                  <Stack gap="sm">
                    <Textarea
                      placeholder="Type your reply..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      rows={3}
                    />
                    <Group justify="space-between">
                      <Group>
                        <ActionIcon variant="subtle">
                          <IconPaperclip size={18} />
                        </ActionIcon>
                        <ActionIcon variant="subtle">
                          <IconPhoto size={18} />
                        </ActionIcon>
                        <ActionIcon variant="subtle">
                          <IconMoodHappy size={18} />
                        </ActionIcon>
                      </Group>
                      <Button
                        leftSection={<IconSend size={16} />}
                        onClick={handleSendReply}
                        disabled={!replyContent.trim()}
                      >
                        Send Reply
                      </Button>
                    </Group>
                  </Stack>
                </Box>
              </>
            ) : (
              <Center h="100%">
                <Stack align="center" gap="md">
                  <ThemeIcon size="xl" variant="light" color="gray">
                    <IconMessage size={32} />
                  </ThemeIcon>
                  <Text c="dimmed">Select a message to view</Text>
                </Stack>
              </Center>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      {/* Compose Message Modal */}
      <Modal
        opened={composeModalOpened}
        onClose={closeComposeModal}
        title="New Message"
        size="lg"
      >
        <Stack gap="md">
          <Select
            label="Recipient Type"
            data={[
              { value: 'parent', label: 'Parent' },
              { value: 'all_parents', label: 'All Parents in Class' },
            ]}
            value={composeForm.recipient_type}
            onChange={(value) => setComposeForm(prev => ({ ...prev, recipient_type: value || 'parent' }))}
          />

          {composeForm.recipient_type === 'parent' && (
            <>
              <Select
                label="Select Student"
                placeholder="Choose a student"
                searchable
                data={[
                  { value: 'student-1', label: 'Emma Johnson' },
                  { value: 'student-2', label: 'Liam Davis' },
                  { value: 'student-3', label: 'Olivia Smith' },
                ]}
                value={composeForm.student_id}
                onChange={(value) => setComposeForm(prev => ({
                  ...prev,
                  student_id: value || '',
                  parent_id: value ? `parent-${value.split('-')[1]}` : '', // Mock: derive parent ID from student ID
                }))}
                required
              />

              {composeForm.student_id && (
                <Alert color="blue" p="xs">
                  <Text size="sm">
                    Message will be sent to: Sarah Johnson (emma.j@email.com)
                  </Text>
                </Alert>
              )}
            </>
          )}

          <TextInput
            label="Subject"
            placeholder="Enter message subject"
            value={composeForm.subject}
            onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
            required
          />

          <Select
            label="Priority"
            data={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'urgent', label: 'Urgent' },
            ]}
            value={composeForm.priority}
            onChange={(value) => setComposeForm(prev => ({ ...prev, priority: value as any }))}
          />

          <Textarea
            label="Message"
            placeholder="Type your message..."
            value={composeForm.content}
            onChange={(e) => setComposeForm(prev => ({ ...prev, content: e.target.value }))}
            rows={6}
            required
          />

          <Group justify="space-between">
            <Group>
              <Button variant="subtle" leftSection={<IconTemplate size={16} />} onClick={openTemplateModal}>
                Use Template
              </Button>
            </Group>
            <Group>
              <Button variant="light" onClick={closeComposeModal}>
                Cancel
              </Button>
              <Button
                leftSection={<IconSend size={16} />}
                onClick={handleSendNewMessage}
                disabled={!composeForm.subject || !composeForm.content || (composeForm.recipient_type === 'parent' && !composeForm.student_id)}
              >
                Send Message
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* Announcement Modal */}
      <Modal
        opened={announcementModalOpened}
        onClose={closeAnnouncementModal}
        title="Create Announcement"
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Announcement Title"
            placeholder="Enter announcement title"
            value={announcementForm.title}
            onChange={(e) => setAnnouncementForm(prev => ({ ...prev, title: e.target.value }))}
            required
          />

          <Select
            label="Type"
            data={[
              { value: 'general', label: 'General' },
              { value: 'event', label: 'Event' },
              { value: 'reminder', label: 'Reminder' },
              { value: 'urgent', label: 'Urgent' },
            ]}
            value={announcementForm.type}
            onChange={(value) => setAnnouncementForm(prev => ({ ...prev, type: value as any }))}
          />

          <Select
            label="Target Audience"
            data={[
              { value: 'all', label: 'All Parents' },
              { value: 'class', label: 'Specific Classes' },
              { value: 'selected', label: 'Selected Students' },
            ]}
            value={announcementForm.target}
            onChange={(value) => setAnnouncementForm(prev => ({ ...prev, target: value as any }))}
          />

          {announcementForm.target === 'class' && (
            <MultiSelect
              label="Select Classes"
              placeholder="Choose classes"
              data={classes?.filter(cls => cls && cls.name).map(cls => ({ value: cls.id.toString(), label: cls.name })) || []}
              value={announcementForm.class_ids}
              onChange={(value) => setAnnouncementForm(prev => ({ ...prev, class_ids: value }))}
            />
          )}

          <Textarea
            label="Content"
            placeholder="Type your announcement..."
            value={announcementForm.content}
            onChange={(e) => setAnnouncementForm(prev => ({ ...prev, content: e.target.value }))}
            rows={6}
            required
          />

          <DatePickerInput
            label="Schedule For (Optional)"
            placeholder="Send immediately if not set"
            value={announcementForm.scheduled_for}
            onChange={(date) => setAnnouncementForm(prev => ({ ...prev, scheduled_for: date }))}
            minDate={new Date()}
          />

          <Group justify="space-between">
            <Button variant="light" onClick={closeAnnouncementModal}>
              Cancel
            </Button>
            <Button
              leftSection={<IconBroadcast size={16} />}
              onClick={handleSendAnnouncement}
              disabled={!announcementForm.title || !announcementForm.content}
            >
              {announcementForm.scheduled_for ? 'Schedule' : 'Send'} Announcement
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Template Modal */}
      <Modal
        opened={templateModalOpened}
        onClose={closeTemplateModal}
        title="Message Templates"
        size="md"
      >
        <Stack gap="md">
          {messageTemplates.map(template => (
            <Card key={template.id} withBorder>
              <Group justify="space-between" mb="xs">
                <Text fw={500}>{template.name}</Text>
                <Badge size="sm" variant="light">{template.category}</Badge>
              </Group>
              <Text size="sm" c="dimmed" mb="xs">{template.subject}</Text>
              <Text size="xs" lineClamp={2}>{template.content}</Text>
              <Button
                size="xs"
                variant="light"
                mt="sm"
                onClick={() => handleUseTemplate(template)}
                fullWidth
              >
                Use This Template
              </Button>
            </Card>
          ))}
        </Stack>
      </Modal>
    </Stack>
  );
};

export default MessagesPage;
